/**
 * Page Performance — report batching + the sender (pure, no React/DOM).
 *
 * Load records and `end` records (CLS / INP) are queued and posted together
 * to POST /page-performance/track:
 *
 *   - as soon as 20 items are queued (the server's per-array cap is 20, so
 *     neither array of one body can exceed it),
 *   - 5 s after the first item of a batch was queued,
 *   - and when the page is hidden / unloaded (the caller calls `flush()`).
 *
 * Items keep their queue order across a split, so an `end` is never sent in
 * an earlier body than its load. A body is kept under ~48 KB (keepalive
 * requests share a 64 KB in-flight budget); a larger batch is halved, and
 * the bodies are sent one after another, never in parallel.
 *
 * Nothing here throws: a failed report changes nothing for the user, and a
 * failed batch is not retried.
 */

export const MAX_ITEMS = 20
export const FLUSH_DELAY_MS = 5_000
export const MAX_AGE_MS = 900_000
export const MAX_BODY_CHARS = 48_000
/** A send that has not settled after this long is treated as finished, so a
 *  hung request never blocks later reports. */
export const RELEASE_MS = 10_000
/** Keepalive requests share a 64 KiB budget; stay just under it. */
export const URGENT_BUDGET_CHARS = 60_000

/** Wire shape of a queued load: the internal clock start becomes `ageMs`
 *  (ms from the open's start to now). Null when too old to be accepted. */
export function toWireLoad(record, nowMs) {
  const { startAt, ...rest } = record
  const age = Math.round(nowMs - startAt)
  if (!Number.isFinite(age) || age > MAX_AGE_MS) return null
  return { ...rest, ageMs: Math.max(0, age) }
}

/** Split queue items into request bodies, each under `maxChars`. */
export function buildPayloads(items, maxChars = MAX_BODY_CHARS) {
  if (!items.length) return []
  const body = { loads: [], ends: [] }
  for (const it of items) (it.type === 'load' ? body.loads : body.ends).push(it.data)
  if (!body.loads.length) delete body.loads
  if (!body.ends.length) delete body.ends
  if (items.length === 1 || JSON.stringify(body).length <= maxChars) return [body]
  const mid = Math.ceil(items.length / 2)
  return [
    ...buildPayloads(items.slice(0, mid), maxChars),
    ...buildPayloads(items.slice(mid), maxChars),
  ]
}

export function createBatcher(deps = {}) {
  const {
    send = () => {},
    now = () => Date.now(),
    setTimer = () => null,
    clearTimer = () => {},
    maxItems = MAX_ITEMS,
    delayMs = FLUSH_DELAY_MS,
    maxBodyChars = MAX_BODY_CHARS,
  } = deps

  let queue = []
  let timer = null
  // Bodies waiting to be sent. Sent ONE AFTER ANOTHER: the next body only
  // after the previous one resolved or failed, so the shared 64 KB keepalive
  // budget is never exceeded and an `end` can never overtake its load.
  let outbox = []
  let sending = false
  let inFlightChars = 0

  function stopTimer() {
    if (timer != null) {
      try {
        clearTimer(timer)
      } catch {
        /* never throw from the tracker */
      }
    }
    timer = null
  }

  const sizeOf = (body) => {
    try {
      return JSON.stringify(body).length
    } catch {
      return Infinity
    }
  }

  function pump() {
    if (sending || !outbox.length) return
    const body = outbox.shift()
    sending = true
    inFlightChars = sizeOf(body)
    let p
    try {
      p = send(body)
    } catch {
      p = null
    }
    let released = false
    let releaseTimer = null
    const next = () => {
      if (released) return
      released = true
      if (releaseTimer != null) {
        try {
          clearTimer(releaseTimer)
        } catch {
          /* never throw from the tracker */
        }
      }
      sending = false
      inFlightChars = 0
      pump()
    }
    if (p && typeof p.then === 'function') {
      p.then(next, next)
      // A request that never settles (a hung connection) must not stop
      // every later report for the life of the tab.
      try {
        releaseTimer = setTimer(next, RELEASE_MS)
      } catch {
        releaseTimer = null
      }
    } else {
      next()
    }
  }

  /** Page hide / account switch: flush, and if a body is still on the wire
   *  let ONE more go out now — when both fit the shared keepalive budget —
   *  so the final reports (the abandoned open and its end) are not left in
   *  the outbox as the page goes away. */
  function flushUrgent() {
    flush()
    try {
      if (sending && outbox.length && inFlightChars + sizeOf(outbox[0]) <= URGENT_BUDGET_CHARS) {
        const body = outbox.shift()
        const p = send(body)
        if (p && typeof p.catch === 'function') p.catch(() => {})
      }
    } catch {
      /* never throw from the tracker */
    }
  }

  function flush() {
    try {
      stopTimer()
      if (!queue.length) return
      const t = now()
      const items = []
      for (const it of queue) {
        if (it.type === 'load') {
          const data = toWireLoad(it.data, t)
          if (data) items.push({ type: 'load', data })
        } else {
          items.push(it)
        }
      }
      queue = []
      outbox.push(...buildPayloads(items, maxBodyChars))
      pump()
    } catch {
      queue = []
    }
  }

  function push(type, data) {
    try {
      if (!data) return
      queue.push({ type, data })
      if (queue.length >= maxItems) {
        flush()
        return
      }
      if (timer == null) timer = setTimer(flush, delayMs)
    } catch {
      /* never throw from the tracker */
    }
  }

  return {
    addLoad: (record) => push('load', record),
    addEnd: (record) => push('end', record),
    flush,
    flushUrgent,
    /** Forget everything queued and not yet sent (the identity behind the
     *  tab changed). A body already on the wire is not recalled. */
    clear() {
      stopTimer()
      queue = []
      outbox = []
    },
    size: () => queue.length,
    /** Bodies built but not yet handed to the transport. */
    pendingBodies: () => outbox.length,
  }
}

/** The transport: a keepalive JSON POST with the session cookie. Returns a
 *  promise that never rejects. */
export function createSender({ fetchImpl, url } = {}) {
  return function sendPagePerf(body) {
    try {
      if (typeof fetchImpl !== 'function' || !url) return Promise.resolve()
      return Promise.resolve(
        fetchImpl(url, {
          method: 'POST',
          keepalive: true,
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        }),
      ).catch(() => {})
    } catch {
      return Promise.resolve()
    }
  }
}
